import React from "react";
import { Sparkles, Moon, Sun, ArrowRight, Share2 } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { useLang } from "@/lib/lang";

export default function Hero() {
  const { lang, t } = useLang();

  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative min-h-[95vh] pt-32 pb-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full mystic-card mb-8">
            <span className="w-2 h-2 rounded-full sparkle" style={{ background: "var(--gold)" }} />
            <span className="overline">{t("hero.badge")}</span>
          </div>

          <h1
            data-testid={HERO.title}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[92px] leading-[1.02] tracking-tight"
          >
            {lang === "fr" ? "Chatbot IA " : "Greco-Celtic "}
            <span className="italic" style={{ color: "var(--gold-glow)" }}>{lang === "fr" ? "Gréco-Celtique" : "AI chatbot"}</span>
          </h1>

          <p className="mt-8 text-lg sm:text-xl max-w-2xl leading-relaxed" style={{ color: "var(--violet-soft)" }}>
            {t("hero.subtitle")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="/decouvrir" data-testid="hero-cta-promo" className="btn-gold inline-flex items-center gap-2">
              <Share2 size={15} />
              {lang === "fr" ? "Découvrir & partager" : "Discover & share"}
              <ArrowRight size={15} />
            </a>
            <a href="#personnages" data-testid={HERO.ctaOracles} className="btn-ghost inline-flex items-center gap-2">
              <Sparkles size={14} />
              {lang === "fr" ? "Rencontrer les personnages" : "Meet the characters"}
            </a>
            <a href="/barde" data-testid={HERO.ctaBarde} className="btn-ghost inline-flex items-center gap-2">
              <Sun size={14} />
              {t("hero.cta.barde")}
            </a>
            <a href="/pub" data-testid="hero-cta-taverne" className="btn-ghost inline-flex items-center gap-2">
              <Moon size={14} />
              {t("taverne.enter")}
            </a>
          </div>


          <div className="mt-14 grid grid-cols-3 gap-6 max-w-2xl">
            {[
              { k: "25+", v: lang === "en" ? "AI characters" : "Personnages IA" },
              { k: "2", v: t("hero.stat.oracles") },
              { k: "100 %", v: lang === "en" ? "Free · unlimited" : "Gratuit · illimité" },
            ].map((m) => (
              <div key={m.v} className="border-l pl-4" style={{ borderColor: "rgba(212, 175, 55, 0.4)" }}>
                <div className="font-serif text-3xl sm:text-4xl" style={{ color: "var(--star)" }}>
                  {m.k}
                </div>
                <div className="text-xs uppercase tracking-[0.28em] mt-1 font-mono" style={{ color: "var(--muted)" }}>
                  {m.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Crystal ball visual */}
        <div className="lg:col-span-5 hidden lg:flex justify-center">
          <div className="relative">
            <div className="absolute -inset-20 rounded-full opacity-60 slowspin" style={{
              background: "conic-gradient(from 0deg, transparent, rgba(212,175,55,0.35), transparent 60%)",
              filter: "blur(30px)",
            }} />
            <div className="crystal-ball halo" />
            <div className="absolute -top-4 -right-4 mystic-card rounded-2xl px-4 py-3 flex items-center gap-3">
              <Moon size={16} className="text-gold" />
              <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--violet-soft)" }}>
                {t("hero.moon")}<br />
                <span style={{ color: "var(--gold)" }}>{t("hero.moon.active")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
